import { DEFAULT_PAGE_SIZE, SortDirection } from '@/components/Table/types';
import { HttpClient } from '@/core/http/http-client';
import { DeleteProfileImageJson } from '@/modules/types';
import {
  mapLabourTimeAutomatedList,
  mapTotalReturnList,
} from '@/shared/mappers/kpi-mapper';
import {
  ApiResponse,
  DataWithPagination,
  LabourTimeAutomatedDetailsJson,
  Page,
  TotalReturnDetailsJson,
} from '@/shared/types';
import { DateUtility } from '@/utils/date.utils';
import { DateRange } from 'react-day-picker';
import { CompanyModel, UserCompany } from '../domain';
import {
  companiesMapper,
  companiesWithPaginationMapper,
  companyAutomationsPerformanceMapper,
  companyMapper,
  userCompanyPageMapper,
} from '../mappers';
import {
  ApiResponseCompanies,
  ApiResponseCompany,
  CompanyAutomationPerformanceJson,
  CompanyJson,
  PagedCompanies,
  UserCompanyJson,
} from '../types';
import { AttachUserCompanyModel } from '../types/attach-user-company-model';
import { DisassociateCompanyUserModel } from '../types/disassociate-company-user-model';
import { CompanyRepository } from './company.repository';

export class HttpCompanyRepository extends CompanyRepository {
  constructor(private readonly client: HttpClient) {
    super();
  }

  async getCompanies(
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<CompanyModel[]>> {
    const companies = await this.client.get<
      ApiResponseCompanies<PagedCompanies>
    >({
      url: '/me/companies',
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return companiesWithPaginationMapper(companies);
  }

  async updateCompany(data: FormData): Promise<CompanyModel> {
    const updatedCompany = await this.client.put<CompanyJson>(
      `${data.get('id')}`,
      data,
    );
    return companyMapper(updatedCompany);
  }

  async createCompany(data: FormData): Promise<CompanyModel> {
    const newCompany = await this.client.post<CompanyJson>({ body: data });
    return companyMapper(newCompany);
  }

  async attachUserCompany(data: AttachUserCompanyModel): Promise<void> {
    return await this.client.post({
      body: data,
      url: `associate-user`,
    });
  }

  async uploadProfilePicture(data: FormData): Promise<unknown> {
    return await this.client.post<unknown>({
      url: `${data.get('EntityId')}/profile-picture`,
      body: data,
    });
  }

  async deleteProfilePicture(data: DeleteProfileImageJson): Promise<unknown> {
    return await this.client.delete<unknown>(
      `${data.entityId}/profile-picture/${data.fileId}`,
    );
  }

  async getCompany(id: number): Promise<CompanyModel> {
    const company = await this.client.get<ApiResponseCompany<CompanyJson>>({
      url: `${id}`,
    });
    return companyMapper(company.data.company);
  }

  async getCompanyAutomationsPerformanceById(
    companyId: number,
    dateRange?: DateRange,
  ) {
    const companyPerformance = await this.client.get<
      ApiResponse<CompanyAutomationPerformanceJson>
    >({
      url: `${companyId}/automations-performance`,
      query: {
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return companyAutomationsPerformanceMapper(companyPerformance.data);
  }

  async enableCompany(id: number): Promise<ApiResponse<unknown>> {
    return this.client.patch(`${id}/enable`);
  }

  async disableCompany(id: number): Promise<ApiResponse<unknown>> {
    return this.client.patch(`${id}/disable`);
  }

  async disassociateUser(data: DisassociateCompanyUserModel): Promise<void> {
    return await this.client.post({
      body: data,
      url: `disassociate-user`,
    });
  }

  async getCompaniesByOrganization(
    organizationId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<CompanyModel[]>> {
    const companies = await this.client.get<
      ApiResponseCompanies<PagedCompanies>
    >({
      url: `/organizations/${organizationId}/companies`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return companiesWithPaginationMapper(companies);
  }

  async getAllCompaniesByOrganization(
    organizationId: number,
  ): Promise<CompanyModel[]> {
    const companies = await this.client.get<
      ApiResponseCompanies<CompanyJson[]>
    >({
      url: `/organizations/${organizationId}/companies/base-info`,
    });

    return companiesMapper(companies.data.companies);
  }

  async getEligibleCompaniesByInfrastructureCost(
    infrastructureCostId: number,
  ): Promise<CompanyModel[]> {
    const companies = await this.client.get<
      ApiResponseCompanies<CompanyJson[]>
    >({
      url: `/infrastructure-costs/${infrastructureCostId}/eligible-companies`,
    });

    return companiesMapper(companies.data.companies);
  }

  async getCompaniesByUser(
    userId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<UserCompany[]>> {
    const response = await this.client.get<
      ApiResponseCompanies<Page<UserCompanyJson>>
    >({
      url: `/users/${userId}/companies`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return userCompanyPageMapper(response.data.companies);
  }

  async getCompaniesByInfrastructureCost(
    infrastructureCostId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<CompanyModel[]>> {
    const companies = await this.client.get<
      ApiResponseCompanies<PagedCompanies>
    >({
      url: `/infrastructure-costs/${infrastructureCostId}/companies`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return companiesWithPaginationMapper(companies);
  }

  async addRatesFile(data: FormData): Promise<unknown> {
    return await this.client.post<unknown>({
      url: `rates`,
      body: data,
    });
  }

  async deleteCompany(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.delete(`${id}`);
  }

  async getCompanyLabourTimeAutomatedById(
    companyId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<
      ApiResponse<LabourTimeAutomatedDetailsJson>
    >({
      url: `${companyId}/labour-time-automated`,
      query: {
        page,
        filter,
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return mapLabourTimeAutomatedList(
      response.data.executions,
      response.data.totals,
    );
  }

  async getCompanyTotalReturnById(
    companyId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<ApiResponse<TotalReturnDetailsJson>>(
      {
        url: `${companyId}/total-return`,
        query: {
          page,
          filter,
          ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
        },
      },
    );

    return mapTotalReturnList(response.data.executions, response.data.totals);
  }

  async getProfilePictureById(companyId: number) {
    return await this.client.get<Blob>({
      url: `${companyId}/profile-picture`,
      config: {
        responseType: 'blob',
      },
    });
  }
}
